import { delay, put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions/dist/type-helpers';
import { RouteComponentProps } from 'react-router';

import { accountOrganizationsReset } from 'CorePackage/AccountModule/actions/accountActions';
import {
  coreGetNotificationInfo,
  coreSetLoading,
  coreSetPublicPortalURL,
} from 'CorePackage/MainModule/actions/coreActions';
import { UserRoleEnum } from 'APIPackage/AuthModule/enums/UserRoleEnum';
import { fetchLogin, fetchLogout, fetchUserInfo, fetchUserInfoDetails } from 'APIPackage/AuthModule/services/authApi';
import { CheckPluginStatusesEnum } from 'CorePackage/MainModule/enums/CheckPluginStatusesEnum';
import { IUserInfo } from 'APIPackage/AuthModule/interfaces/IUserInfo';
import { fetchPublicPortalURL } from 'APIPackage/MainModule/services/portalApi';
import { IS_ESIA, LOGIN, ORGANIZATION, RETURN_URL } from 'CorePackage/MainModule/constants/localStorageNames';
import { ROUTE_AUTH, ROUTE_LK } from 'CorePackage/MainModule/routes/routes';
import {
  getStatusInfoRead,
  IStatusInfoProps,
  setStatusInfoRead,
} from 'APIPackage/MainModule/services/statusInfoReadApi';
import { init } from 'KnmPackage/MainModule/common/cryptoHelpers';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { CODE, STATE } from 'CorePackage/MainModule/constants/auth';
import {
  fetchEsiaGetOrganizationsByAuthCode,
  fetchEsiaLogout,
  fetchEsiaSegmentRegistry,
  fetchEsiaSessionByAuthCode,
} from 'APIPackage/AuthModule/services/esiaApi';
import { IUserOrganization } from 'APIPackage/AuthModule/interfaces/IUserOrganization';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ISegment } from 'APIPackage/AuthModule/interfaces/ISegment';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { ISession } from 'APIPackage/AuthModule/interfaces/ISession';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { setStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { userNotificationUnreadGetCount } from 'UserNotificationPackage/MainModule/actions/UserNotificationAction';

import {
  authInitEsiaAuthProcess,
  authLogin,
  authSetError,
  authSetIsLoggedOut,
  authSetOrganizations,
  authSetStage,
  setUserAction,
} from '../actions/authActions';
import { AuthActionsEnum } from '../enums/AuthActionsEnum';
import { onSelectOrganization } from '../common/onSelectOrganization';
import { renderErrorNotices } from '../common/renderErrorNotices';
import { getAuthOrganizations } from '../selectors/authSelectors';

export function* watchAuthSaga() {
  yield takeEvery(AuthActionsEnum.CLEAR_USER, authClearUserSaga);
  yield takeEvery(AuthActionsEnum.AUTH_LOGIN, authLoginSaga);
  yield takeEvery(AuthActionsEnum.AUTH_INIT_ESIA_AUTH_PROCESS, authInitEsiaAuthProcessSaga);
  yield takeEvery(AuthActionsEnum.AUTH_LOGOUT, authLogoutSaga);
}

function* authClearUserSaga() {
  yield put(accountOrganizationsReset());
}

function* getUserInfo(history: RouteComponentProps['history']) {
  const allRoles = Object.values(UserRoleEnum);
  const userInfo: IUserInfo = yield fetchUserInfo();
  const params: { isTestUser: boolean } = yield fetchUserInfoDetails();
  // const params = {
  //   isTestUser: true,
  // };
  const roles = userInfo.roles.filter(r => allRoles.includes(r.sysname));
  const useData: IUserInfo = {
    ...userInfo,
    userProfile: {
      ...userInfo.userProfile,
      isTestUser: params.isTestUser,
    },
    roles,
  };

  yield put(setUserAction(useData));

  yield put(userNotificationUnreadGetCount());

  const url: string = yield fetchPublicPortalURL();

  yield put(coreSetPublicPortalURL(url));
  const returnUrl = sessionStorage.getItem(RETURN_URL);

  history.push(ROUTE_LK);
  if (returnUrl) {
    yield put(coreSetLoading(true));
    yield delay(2000);
    history.push(returnUrl);
    yield put(coreSetLoading(false));
  }
  sessionStorage.setItem(LOGIN, userInfo.userAccount.login);
  sessionStorage.removeItem(RETURN_URL);

  return useData;
}

function* authLoginSaga(action: ActionType<typeof authLogin>) {
  function* funcTry() {
    const { username, password, history } = action.payload;

    yield fetchLogin(username, password);
    const userInfo: IUserInfo = yield getUserInfo(history);
    const statusInfoRead: IStatusInfoProps = yield getStatusInfoRead();

    if (
      statusInfoRead?.status === CheckPluginStatusesEnum.AGREE &&
      userInfo.roles.find(item => item.sysname === UserRoleEnum.SUPERVISOR)
    ) {
      yield init();
      yield setStatusInfoRead({ status: CheckPluginStatusesEnum.AGREE });
    }
    yield put(coreGetNotificationInfo());
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      yield put(authSetError(err));
    },
  });
}

function* authByCodeAnState(history: RouteComponentProps['history']) {
  const organizationsFromStore: IUserOrganization[] | undefined = yield select(getAuthOrganizations);

  function* funcTry() {
    const organizationFromStorage = sessionStorage.getItem(ORGANIZATION);
    const organization =
      organizationFromStorage && organizationFromStorage !== 'undefined'
        ? JSON.parse(organizationFromStorage)
        : undefined;
    const segments: ISegment[] = yield fetchEsiaSegmentRegistry(organization?.OGRN);

    if (segments.length === 1 || segments.length === 0) {
      const searchParams = new URLSearchParams(window.location.search);
      const stateEsia = searchParams.get(STATE) ?? undefined;
      const code = searchParams.get(CODE) ?? undefined;
      const session: ISession = yield fetchEsiaSessionByAuthCode(
        code,
        stateEsia,
        organization,
        segments.length ? segments[0].id : undefined,
        segments.length === 0 ? true : undefined,
      );

      if (session.userAccountStatus === 'REGISTERED') {
        yield put(
          addNoticeAction({
            renderContent:
              'Пользователь зарегистрирован. Для дальнейшей работы требуется активация системным администратором.',
            noticeType: NoticeTypesEnum.SUCCESS,
            messageType: MessageTypesEnum.REGISTER_USER,
          }),
        );
      } else {
        sessionStorage.setItem(IS_ESIA, 'true');
        yield getUserInfo(history);
      }
    } else {
      yield put(
        addNoticeAction({
          renderContent: 'Найдено более одного сегмента для выбранной организации',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.ESIA_AUTHORIZATION,
        }),
      );
      throw new Error();
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield renderErrorNotices(organizationsFromStore);
    },
    *funcFinally() {
      yield sessionStorage.removeItem(ORGANIZATION);
    },
  });
}

function* authInitEsiaAuthProcessSaga(action: ActionType<typeof authInitEsiaAuthProcess>) {
  const { history } = action.payload;
  const state: IRootState = yield select();
  const searchParams = new URLSearchParams(window.location.search);
  const stateEsia = searchParams.get(STATE);
  const code = searchParams.get(CODE);
  const organizationsFromStore = getAuthOrganizations(state);
  const organizationFromSessionStorage = sessionStorage.getItem(ORGANIZATION);
  const organization =
    organizationFromSessionStorage && organizationFromSessionStorage !== 'undefined'
      ? JSON.parse(organizationFromSessionStorage)
      : undefined;

  if (code && stateEsia) {
    if (!organization) {
      yield put(authSetStage('select'));
      try {
        const orgs: IUserOrganization[] = yield fetchEsiaGetOrganizationsByAuthCode(code, stateEsia);

        yield put(authSetOrganizations(orgs));

        if (orgs.length === 0) {
          yield renderErrorNotices(orgs);
        }

        if (orgs.length === 1) {
          yield put(coreSetLoading(true));
          onSelectOrganization(orgs[0]);
        }
      } catch (err) {
        yield renderErrorNotices(organizationsFromStore);
        sessionStorage.removeItem(ORGANIZATION);
        history.push(ROUTE_AUTH);
        yield put(authSetStage('login'));
      }
    } else {
      yield authByCodeAnState(history);
    }
  }
}

function* authLogoutSaga() {
  function* funcTry() {
    setStorageItem(StorageNamesEnum.USER_FIRST_LOGIN, false);
    sessionStorage.setItem(ORGANIZATION, 'undefined');
    const isEsia = sessionStorage.getItem(IS_ESIA) === 'true';

    yield put(accountOrganizationsReset());
    if (isEsia) {
      yield fetchEsiaLogout();
    } else {
      yield fetchLogout();
    }
    yield put(authSetIsLoggedOut(true));
  }

  yield sagaWrapper({
    funcTry,
  });
}
